import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useApi } from "../api/context";
import { ApiError } from "../api/errors";
import type { User } from "../api/types";
import { sessionStore, useSession } from "./session";

type Auth = { user: User | null; login: (u: string, p: string) => Promise<void>; logout: () => void };
const Ctx = createContext<Auth | null>(null);
export function useAuth(): Auth {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

/** Only platform operators may use the console (§15); tenant staff are turned away. */
export const NOT_PLATFORM = "This console is for BaraPOS platform administrators. Staff of a business sign in to the BaraPOS app instead.";

export function AuthProvider({ children }: { children: ReactNode }) {
  const { api } = useApi();
  const qc = useQueryClient();
  const session = useSession();
  const token = session?.token ?? null;

  useEffect(() => {
    if (!token) return;
    api.auth.me().then(
      (u) => {
        if (u.role !== "SUPERADMIN") sessionStore.set(null);
        else if (sessionStore.token() === token) sessionStore.set({ token, user: u });
      },
      () => undefined,
    );
  }, [api, token]);

  const login = useCallback(
    async (username: string, password: string) => {
      const { token: t, ...user } = await api.auth.login(username, password);
      if (user.role !== "SUPERADMIN") throw new ApiError(403, NOT_PLATFORM);
      qc.clear();
      sessionStore.set({ token: t, user });
    },
    [api, qc],
  );
  const logout = useCallback(() => {
    sessionStore.set(null);
    qc.clear();
  }, [qc]);
  const value = useMemo(() => ({ user: session?.user ?? null, login, logout }), [session, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}
